import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';

import { BoardMemberCard } from '../components/BoardMemberCard';
import { PageSeo } from '../components/PageSeo';
import { SubpageLayout } from '../components/SubpageLayout';
import { USE_STATIC_SITE_CONTENT } from '../config/staticSiteMode';
import { STATIC_PAST_PRESIDENTS } from '../data/staticSite/content';
import { useAuth } from '../context/AuthContext';
import { mapPastPresidentsFallbackFromI18n } from '../data/pastPresidents';

const PAST_PRESIDENT_SKELETON_COUNT = 6;

function PastPresidentsPageSkeleton({ statusLabel }) {
    return (
        <div role="status" aria-live="polite" aria-busy="true">
            <span className="visually-hidden">{statusLabel}</span>
            <SkeletonTheme
                baseColor="#e8ecf2"
                highlightColor="#f0f3f8"
            >
                <ul className="past-presidents-grid past-presidents-grid--skeleton list-unstyled row g-3 g-md-4 mb-0">
                    {Array.from(
                        { length: PAST_PRESIDENT_SKELETON_COUNT },
                        (_, i) => (
                            <li
                                key={`past-president-sk-${i}`}
                                className="col-6 col-md-4 col-lg-2"
                                aria-hidden
                            >
                                <article className="board-member-card board-member-card--skeleton">
                                    <Skeleton
                                        height={48}
                                        width="100%"
                                        style={{
                                            borderRadius: '1rem 1rem 0 0',
                                        }}
                                    />
                                    <div className="board-member-card-photo-wrap">
                                        <Skeleton
                                            height={200}
                                            width="100%"
                                            borderRadius={10}
                                        />
                                    </div>
                                    <div className="px-3 py-3">
                                        <Skeleton
                                            height={18}
                                            width="78%"
                                            className="mx-auto"
                                            borderRadius={6}
                                        />
                                    </div>
                                </article>
                            </li>
                        ),
                    )}
                </ul>
            </SkeletonTheme>
        </div>
    );
}

/**
 * Geçmiş dönem başkanları — URL: /past-presidents
 */
export function PastPresidentsPage() {
    const { t } = useTranslation();
    const { authToken } = useAuth();
    /** `null` = yükleme; `[]` = API boş/hata, i18n fallback */
    const [items, setItems] = useState(
        USE_STATIC_SITE_CONTENT ? STATIC_PAST_PRESIDENTS : null,
    );

    const fallbackItems = useMemo(() => {
        const raw = t('pastPresidents.fallbackItems', { returnObjects: true });
        return mapPastPresidentsFallbackFromI18n(raw);
    }, [t]);

    useEffect(() => {
        if (USE_STATIC_SITE_CONTENT) {
            setItems(STATIC_PAST_PRESIDENTS);
            return undefined;
        }

        let cancelled = false;

        /*
        (async () => {
            try {
                const raw = await fetchCmsPastPresidentsList({
                    accessToken: authToken,
                });
                const mapped = raw
                    .map(mapCmsPastPresidentToListItem)
                    .filter((p) => p.period && p.name);
                if (cancelled) return;
                setItems(mapped.length > 0 ? mapped : []);
            } catch {
                if (!cancelled) setItems([]);
            }
        })();
        */

        return () => {
            cancelled = true;
        };
    }, [authToken]);

    const loading = items === null;
    const displayItems = USE_STATIC_SITE_CONTENT
        ? items ?? []
        : !loading && items.length > 0
          ? items
          : fallbackItems;

    return (
        <>
            <PageSeo
                title={`${t('pastPresidents.title')} — ${t('header.rotaryAlt')}`}
                description={t('seo.pastPresidents')}
            />
            <SubpageLayout
                title={t('pastPresidents.title')}
                subtitle={t('pastPresidents.subtitle')}
                contentClassName="past-presidents-page"
            >
            {loading ? (
                <PastPresidentsPageSkeleton
                    statusLabel={t('pastPresidents.loading')}
                />
            ) : (
                <ul className="past-presidents-grid list-unstyled row g-3 g-md-4 mb-0">
                    {displayItems.map((p) => (
                        <li
                            key={
                                'id' in p && p.id
                                    ? p.id
                                    : `${p.period}-${p.name}`
                            }
                            className="col-6 col-md-4 col-lg-2"
                        >
                            <BoardMemberCard
                                role={p.period}
                                name={p.name}
                                image={p.image}
                            />
                        </li>
                    ))}
                </ul>
            )}
        </SubpageLayout>
        </>
    );
}
